import { useState, useEffect, useRef, useCallback } from 'react'

import { useAuth0 } from '@auth0/auth0-react'
import axios from 'axios'

import { useAuth } from '@/hooks'
import { exchangeTokenWithRetry, isTokenExpired, saveToken } from '@/services/authService'

const IDLE_TIMEOUT_MS = Number(import.meta.env.VITE_IDLE_TIMEOUT_MS) || 15 * 60 * 1000 // 15 minutes
const TOKEN_CHECK_INTERVAL_MS = 5_000 // Check token health every 5 seconds while idle
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'touchstart', 'click', 'scroll'] as const

type ScreensaverMode = 'checking' | 'idle' | 'expired'

export const Screensaver = () => {
  const { needsHardLogin, setNeedsHardLogin } = useAuth()
  const { loginWithPopup, loginWithRedirect, getAccessTokenSilently } = useAuth0()
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [isIdle, setIsIdle] = useState(false)
  const [mode, setMode] = useState<ScreensaverMode>('checking')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Reset the idle timer on user activity
  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    timerRef.current = setTimeout(() => {
      setIsIdle(true)
    }, IDLE_TIMEOUT_MS)
  }, [])

  // Set up activity listeners for idle detection
  useEffect(() => {
    resetTimer()
    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, resetTimer, { passive: true })
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, resetTimer)
      }
    }
  }, [resetTimer])

  // When idle state triggers, check if Auth0 session is still valid
  useEffect(() => {
    if (!isIdle) return

    setMode('checking')
    let cancelled = false

    getAccessTokenSilently()
      .then(() => {
        if (!cancelled) setMode('idle')
      })
      .catch(() => {
        if (!cancelled) setMode('expired')
      })

    return () => {
      cancelled = true
    }
  }, [isIdle, getAccessTokenSilently])

  // Periodically check if the app token has expired/been deleted while showing "Welcome Back".
  // If expired, try to silently refresh via Auth0. Only show "Session Expired" if that fails.
  const isRefreshingRef = useRef(false)
  useEffect(() => {
    if (!isIdle || mode !== 'idle') return

    const interval = setInterval(async () => {
      const token = localStorage.getItem('MEGA_API_TOKEN')
      if (!token || isTokenExpired(token)) {
        if (isRefreshingRef.current) return
        isRefreshingRef.current = true
        try {
          const auth0Token = await getAccessTokenSilently()
          const result = await exchangeTokenWithRetry(auth0Token, 2)
          localStorage.setItem('MEGA_API_TOKEN', result.token)
          saveToken(result.token)
          // Token refreshed silently — stay on "Welcome Back"
        } catch {
          // Auth0 session or backend unavailable — switch to expired
          setMode('expired')
        } finally {
          isRefreshingRef.current = false
        }
      }
    }, TOKEN_CHECK_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [isIdle, mode, getAccessTokenSilently])

  // Dismiss on user interaction when auth is still valid
  const handleDismiss = useCallback(() => {
    setIsIdle(false)
    setMode('checking')
    resetTimer()
  }, [resetTimer])

  // Handle re-login when Auth0 session has expired
  const handleLogin = async () => {
    setIsLoggingIn(true)
    try {
      await loginWithPopup()
      const auth0Token = await getAccessTokenSilently({ cacheMode: 'off' })

      const serverUrl =
        window.VITE_MEGALODON_SERVER_URL || import.meta.env.VITE_MEGALODON_SERVER_URL || 'http://localhost:3000'
      const { data } = await axios.post<{ token: string }>(
        `${serverUrl}/auth/token`,
        {},
        { headers: { Authorization: `Bearer ${auth0Token}` }, timeout: 15_000 }
      )

      if (!data?.token) {
        throw new Error('Token exchange returned no token after popup login')
      }

      localStorage.setItem('MEGA_API_TOKEN', data.token)
      saveToken(data.token)

      setNeedsHardLogin(false)
      setIsIdle(false)
      setMode('checking')
      setIsLoggingIn(false)
      resetTimer()

      // Trigger React Query refetch
      window.dispatchEvent(new Event('focus'))
    } catch (err) {
      console.error('Popup login failed, falling back to redirect:', err)
      // Clear the expired token so AuthProvider does a fresh exchange after redirect
      localStorage.removeItem('MEGA_API_TOKEN')
      // Keep isLoggingIn=true — page is about to redirect, don't flash back to "Session Expired"
      const returnTo = window.location.pathname + window.location.search + window.location.hash
      await loginWithRedirect({ appState: { returnTo } })
    }
  }

  // Dismiss on any key press when idle
  useEffect(() => {
    if (!isIdle || (needsHardLogin ? 'expired' : mode) !== 'idle') return
    const onKey = () => handleDismiss()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isIdle, mode, needsHardLogin, handleDismiss])

  // Show screensaver if idle OR if a 401 forced needsHardLogin
  const showScreensaver = isIdle || needsHardLogin
  const effectiveMode = needsHardLogin ? 'expired' : mode

  if (!showScreensaver) return null
  if (effectiveMode === 'checking') return null

  return (
    // Above everything portaled to <body>: panels and modals (z-50), the floor plan viewer
    // (z-[1000]) and dropdown menus (9999).
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-base-300/80 backdrop-blur-sm"
      onClick={effectiveMode === 'idle' ? handleDismiss : undefined}
    >
      <div className="card w-96 bg-base-100 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="card-body items-center text-center">
          {effectiveMode === 'idle' ? (
            <>
              <h2 className="card-title text-2xl">Welcome Back</h2>
              <p className="py-4">Click anywhere or press any key to continue.</p>
            </>
          ) : (
            <>
              <h2 className="card-title text-2xl">Session Expired</h2>
              <p className="py-4">Your session has expired. Please log back in to continue.</p>
              <div className="card-actions">
                <button className="btn btn-primary" onClick={handleLogin} disabled={isLoggingIn}>
                  {isLoggingIn ? (
                    <>
                      <span className="loading loading-spinner loading-sm" />
                      Logging in...
                    </>
                  ) : (
                    'Log Back In'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
